import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { apiGet, apiPost } from '../services/api';

const AuthContext = createContext(null);

export const ROLES = {
  ADMIN: 'administrador',
  GESTOR: 'gestor',
  SUPERVISOR: 'supervisor',
  DIRETOR: 'diretor',
  COORDENADOR: 'coordenador',
  SECRETARIO: 'secretario',
  REFERENCIA_TECNICA: 'referencia_tecnica',
  PROFESSOR: 'professor',
};

// Hierarquia de roles (maior número = maior permissão)
export const ROLE_HIERARCHY = {
  [ROLES.PROFESSOR]: 1,
  [ROLES.GESTOR]: 1,
  [ROLES.REFERENCIA_TECNICA]: 2,
  [ROLES.COORDENADOR]: 3,
  [ROLES.SECRETARIO]: 3,  // mesmo nível que coordenador
  [ROLES.SUPERVISOR]: 4,
  [ROLES.DIRETOR]: 4,     // mesmo nível que supervisor
  [ROLES.ADMIN]: 5,
};

// Secretário tem exatamente as mesmas permissões de rota que Coordenador.
// hasAnyRole normaliza este alias antes de checar o array de roles.
const ROLE_ALIASES = {
  secretario: 'coordenador',
  diretor: 'supervisor',
};

/**
 * Diretor(a) Escolar tem o acesso do Supervisor: as escolas atribuídas a
 * ele, sem criar registros. Toda checagem de menu, rota e permissão por
 * escola usa este papel — o backend faz o mesmo em `is_supervisor`.
 *
 * Só o Diretor entra aqui. O Secretário fica de fora de propósito: ele
 * enxerga como Coordenador mas não escreve, e `hasRole(COORDENADOR)` é usado
 * para liberar escrita.
 */
export const papelDeAcesso = (role) => (role === 'diretor' ? 'supervisor' : role);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Carrega dados do localStorage na inicialização e revalida o perfil
  // contra o servidor.
  //
  // O `userData` do localStorage é uma foto tirada no momento do login. Se o
  // papel do usuário for alterado depois, o aparelho que não deslogou continua
  // navegando com o papel antigo — o que já causou um caso real de sessão
  // exibindo "Referência Técnica" no celular e "Coordenador" no computador,
  // com o menu liberando telas indevidas. Buscar /users/me/ na abertura faz o
  // papel local seguir sempre o servidor.
  //
  // A credencial em si não está aqui: ela vive num cookie httpOnly que o
  // JavaScript não alcança. O que o localStorage guarda é só a foto do
  // usuário — se ela existe, houve um login neste aparelho, e é o servidor
  // quem diz se a sessão ainda vale.
  useEffect(() => {
    const storedUser = localStorage.getItem('userData');

    if (!storedUser) {
      setLoading(false);
      return;
    }

    let cached = null;
    try {
      cached = JSON.parse(storedUser);
    } catch (e) {
      // Se houver erro ao parsear, limpa o storage
      localStorage.removeItem('userData');
      setLoading(false);
      return;
    }

    // O cache entra como valor inicial, mas `loading` só é liberado quando a
    // revalidação termina — assim nenhuma tela chega a ser montada com um
    // papel desatualizado.
    setUser(cached);

    let cancelled = false;
    apiGet('/users/me/')
      .then(fresh => {
        if (cancelled || !fresh) return;
        // Mescla em vez de substituir: o payload do login carrega campos que
        // /users/me/ não devolve (`assigned_schools` e `assigned_school_ids`,
        // usados nos filtros de Supervisor). Trocar o objeto inteiro os
        // apagaria a cada abertura do app.
        const merged = { ...cached, ...fresh };
        localStorage.setItem('userData', JSON.stringify(merged));
        setUser(merged);
      })
      .catch(err => {
        if (cancelled) return;
        // 401/403: o cookie não vale mais (expirou, ou alguém saiu em outro
        // aparelho) — encerra a sessão aqui também. Qualquer outro erro
        // (rede, API fora do ar) mantém o cache, para não deslogar o usuário
        // por uma falha temporária.
        if (err?.status === 401 || err?.status === 403) {
          localStorage.removeItem('userData');
          setUser(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  // O login não recebe mais token: ele já veio no cookie, posto pelo servidor.
  const login = (userData) => {
    localStorage.setItem('userData', JSON.stringify(userData));
    setUser(userData);
  };

  const updateUser = (partial) => {
    setUser(prev => {
      const next = { ...(prev || {}), ...(partial || {}) };
      localStorage.setItem('userData', JSON.stringify(next));
      return next;
    });
  };

  // Sair agora precisa do servidor: só ele apaga o cookie e invalida o token.
  // A tela é limpa de todo jeito — se a chamada falhar, quem está no aparelho
  // não pode ficar preso numa sessão que ele já pediu para encerrar.
  const logout = async () => {
    try {
      await apiPost('/logout/', {});
    } catch (e) {
      console.warn('Falha ao encerrar a sessão no servidor:', e);
    }
    localStorage.removeItem('userData');
    setUser(null);
  };

  const isAuthenticated = () => {
    return !!user;
  };

  const hasRole = (requiredRole) => {
    if (!user) return false;
    return user.role === requiredRole || papelDeAcesso(user.role) === requiredRole;
  };

  const hasMinRole = (minRole) => {
    if (!user) return false;
    const userLevel = ROLE_HIERARCHY[user.role] || 0;
    const requiredLevel = ROLE_HIERARCHY[minRole] || 0;
    return userLevel >= requiredLevel;
  };

  const hasAnyRole = (roles) => {
    if (!user) return false;
    const effectiveRole = ROLE_ALIASES[user.role] || user.role;
    return roles.includes(user.role) || roles.includes(effectiveRole);
  };

  /**
   * Objeto da cidade do usuário logado, memoizado.
   *
   * IMPORTANTE: mantém a MESMA referência enquanto a cidade não muda.
   * Antes esta informação era criada com um objeto literal novo a cada
   * chamada de `getUserCity()`. Como várias telas colocam esse objeto
   * direto no array de dependências de `useEffect`/`useCallback`
   * (ex.: AlunosTEA, IDEB por escola), a referência trocava a cada
   * render e disparava um novo fetch em loop — deixando a tela
   * "carregando pra sempre" (Alunos AEE) ou "piscando" (IDEB por escola).
   */
  const userCity = useMemo(() => {
    if (!user || !user.city) return null;
    return {
      id: user.city,
      name: user.city_name || 'Cidade não identificada',
    };
  }, [user]);

  /**
   * Retorna a cidade do usuário logado
   * @returns {Object|null} Objeto com id e nome da cidade ou null
   */
  const getUserCity = useCallback(() => userCity, [userCity]);

  /**
   * Verifica se o usuário tem cidade vinculada
   * @returns {boolean}
   */
  const hasCity = () => {
    return user?.city != null;
  };

  const value = {
    user,
    loading,
    login,
    logout,
    updateUser,
    isAuthenticated,
    hasRole,
    hasMinRole,
    hasAnyRole,
    getUserCity,
    hasCity,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
}

export default AuthContext;
